'use client'

import Image from 'next/image'
import Link from 'next/link'
import AutoScroll from 'embla-carousel-auto-scroll'
import { IProfileLink } from '@/lib/homePageFunctions'
import FlowbiteIconComponent from '../flowbite-icon/flowbiteIcon'
import { useEmblaCarouselResponsive } from '@/utils/react-hooks/useEmblaCarousel'

interface IProps {
  profileList?: IProfileLink[]
}

const FeaturedProfilesInCampaign = ({ profileList }: IProps) => {
  const { emblaRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext } = useEmblaCarouselResponsive({
    options: { loop: true, align: 'start', watchDrag: false },
    plugins: [AutoScroll({ playOnInit: true, speed: 0.5 })],
    breakpoints: [
      {
        query: '(min-width: 1024px)',
        options: { loop: true, align: 'start', watchDrag: true },
        plugins: [], // disable AutoScroll on large screens
      },
    ],
  })

  return (
    <div className="flex flex-col justify-center items-start w-full">
      <div className="text-white text-[20px] font-semibold leading-[30px] self-start">
        Featured Profiles:
      </div>

      <div className="flex w-full pt-[13px] md:pt-[26px]">
        {/* Left Button */}
        <div
          className={`hidden lg:block mr-[5px] z-10 transition-opacity duration-300 ease-in-out md:pt-[32.5px] ${
            canScrollPrev ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="h-[35px] w-[35px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Scroll left"
          >
            <FlowbiteIconComponent
              iconType="AngleDown"
              className="rotate-90 text-white !ml-0"
              iconClassName="w-6 h-6"
            />
          </button>
        </div>

        {/* Carousel */}
        <div className="w-full overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {profileList?.map((profile, index) => (
              <div
                key={`${profile.slug}-${index}`}
                className="flex-[0_0_auto] flex-shrink-0 flex justify-start items-start flex-col md:mr-[40px] mr-[30px]"
              >
                <Link href={`/individual/${profile.slug}`} target="_blank" prefetch={false}>
                  <div className="flex justify-center items-center flex-col md:w-[100px] w-[65px]  ">
                    <div className="relative mb-2">
                      <Image
                        src={profile.profileImage}
                        alt={profile.fullName}
                        className=" md:h-[100px] h-[65px] rounded-full object-cover shadow-md"
                        width={100}
                        height={100}
                      />
                    </div>
                    <span className="flex justify-center text-white text-center md:text-base text-xs font-semibold leading-[125%] min-h-[2.5rem] md:min-h-[3rem]">
                      {profile.fullName}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Right Button */}
        <div
          className={`hidden lg:block ml-[5px] z-10 transition-opacity duration-300 md:pt-[32.5px] ease-in-out ${
            canScrollNext ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="h-[35px] w-[35px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Scroll right"
          >
            <FlowbiteIconComponent
              iconType="AngleDown"
              className="-rotate-90 text-white !ml-0"
              iconClassName="w-6 h-6"
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default FeaturedProfilesInCampaign
